<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { MenuPayload, MenuResponse } from "@/types/interface/menu";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { mapOptionMultiselect } from "@/utils/helper";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useMenuStore } from "@/stores/master/menu";

const idMenu = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const menuStore = useMenuStore();
const detailMenu = ref<MenuResponse>();
const listParentMenu = ref<any>();

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    url: zod.string().nonempty({ message: "Url is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: url } = useField<string>("url");
const { value: icon } = useField<string>("icon");
const { value: slug } = useField<string>("slug");
const { value: parent_id } = useField<number>("parent_id");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadMenu: MenuPayload = {
    name: name.value,
    url: url.value,
    icon: icon.value,
    slug: slug.value,
    parent_id: parent_id.value?.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await menuStore.dispatchUpdate(
      idMenu.value,
      payloadMenu
    );
  } else {
    responseApi.value = await menuStore.dispatchCreate(payloadMenu);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value ? "Menu Successfully Updated" : "Menu Successfully Added"
      );
      router.push("/master/menu");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadListMenuParent = async () => {
  const { success, message } = await menuStore.dispatchGetAll(1, 99);
  if (success) {
    listParentMenu.value = mapOptionMultiselect(
      menuStore.menus?.items,
      "id",
      "name"
    );
  }
};

const loadDetailMenu = async () => {
  const { success, message } = await menuStore.dispatchDetail(idMenu.value);
  if (success) {
    detailMenu.value = await menuStore?.menu?.data;
    name.value = detailMenu.value?.name;
    url.value = detailMenu.value?.url;
    slug.value = detailMenu.value?.slug;
    icon.value = detailMenu.value?.icon;
    parent_id.value = mapOptionMultiselect(
      detailMenu.value?.parent,
      "id",
      "name"
    );
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idMenu.value = router.currentRoute.value.params.id.toString();
    loadDetailMenu();
  }
  loadListMenuParent();
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Menu</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/menu"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Menu
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="url" class="form-label label text-secondary"
                    >Url <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="url"
                      v-model="url"
                      placeholder="Url"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.url }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="icon" class="form-label label text-secondary"
                    >Icon <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="icon"
                      v-model="icon"
                      placeholder="icon"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.icon }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="parent_id" class="form-label label text-secondary"
                    >Menu Parent <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="parent_id"
                      :options="listParentMenu"
                      placeholder="Menu Parent"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.parent_id }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
